import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import { api } from "@/lib/api";

const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CHAMPAGNE = "#C5A880";

const PROFESSIONS = [
  "Conseiller financier", "Courtier hypothécaire", "Comptable",
  "Fiscaliste", "Recruteur", "Consultant",
  "Entrepreneur", "Autre",
];

const CHALLENGES = [
  "Prioriser mes dossiers du jour",
  "Anticiper les risques clients",
  "Détecter les opportunités que je manque",
  "Structurer mes recommandations",
  "Suivre ma trajectoire de revenus",
  "Autre",
];

/**
 * OnboardingDiagnostic — mini-wizard 3 étapes déclenché depuis la landing
 * (CTA « Commencer un diagnostic »). Capture profession + défi + coordonnées.
 */
export default function OnboardingDiagnostic() {
  const [step, setStep] = useState(0);
  const [profession, setProfession] = useState("");
  const [challenge, setChallenge] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api.post("/landing/diagnostic", {
        profession, biggest_challenge: challenge, email, name,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const canNext = () => (step === 0 && profession) || (step === 1 && challenge);

  return (
    <div className="min-h-screen" style={{ background: CANVAS }} data-testid="onboarding-root">
      <nav className="border-b" style={{ borderColor: HAIRLINE }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <AltioraTreeLogo size={26} />
            <span className="font-serif text-[17px]" style={{ color: INK }}>ALTIORA</span>
          </Link>
          <Link to="/" className="text-[13px] hover:opacity-70" style={{ color: INK_SOFT }} data-testid="onb-back">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> Retour à l'accueil
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="onb-thanks">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-6"
                 style={{ background: `${GOLD}22`, color: GOLD }}>
              <Check className="h-6 w-6" strokeWidth={2} />
            </div>
            <h1 className="font-serif text-[36px] md:text-[48px] leading-tight tracking-tight" style={{ color: INK }}>
              Merci. <span style={{ color: GOLD, fontStyle: "italic" }}>Nous vous répondons sous 48 h.</span>
            </h1>
            <p className="mt-6 text-[16px] leading-[1.7] max-w-xl" style={{ color: INK_SOFT }}>
              Un membre de l'équipe ALTIORA reviendra vers vous avec un premier
              diagnostic personnalisé de votre situation et les recommandations les
              plus pertinentes pour votre profession.
            </p>
          </motion.div>
        ) : (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.32em]" style={{ color: CHAMPAGNE }}
                 data-testid="onb-step">
              Étape {step + 1} / 3
            </div>
            <h1 className="mt-6 font-serif text-[36px] md:text-[52px] leading-[1.05] tracking-tight" style={{ color: INK }}>
              {step === 0 && <>Quel est votre <span style={{ color: GOLD, fontStyle: "italic" }}>métier</span> ?</>}
              {step === 1 && <>Quel est votre <span style={{ color: GOLD, fontStyle: "italic" }}>plus grand défi</span> aujourd'hui ?</>}
              {step === 2 && <>Où vous <span style={{ color: GOLD, fontStyle: "italic" }}>envoyer</span> les résultats ?</>}
            </h1>

            {step === 0 && (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="onb-professions">
                {PROFESSIONS.map((p) => (
                  <button key={p} onClick={() => setProfession(p)} data-testid={`onb-prof-${p}`}
                          className="text-left rounded-lg px-4 py-3 text-[14px] transition-all"
                          style={{
                            background: profession === p ? INK : "transparent",
                            color: profession === p ? IVORY : INK,
                            border: `1px solid ${profession === p ? INK : HAIRLINE}`,
                          }}>
                    {p}
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="mt-10 space-y-2" data-testid="onb-challenges">
                {CHALLENGES.map((c) => (
                  <button key={c} onClick={() => setChallenge(c)} data-testid={`onb-chal-${c.slice(0, 20)}`}
                          className="w-full text-left rounded-lg px-4 py-4 text-[15px] transition-all"
                          style={{
                            background: challenge === c ? INK : "transparent",
                            color: challenge === c ? IVORY : INK,
                            border: `1px solid ${challenge === c ? INK : HAIRLINE}`,
                          }}>
                    {c}
                  </button>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="mt-10 space-y-4 max-w-md" data-testid="onb-contact">
                <input placeholder="Votre prénom" value={name} onChange={(e) => setName(e.target.value)}
                       className="w-full rounded-lg px-4 py-3 text-[15px]"
                       style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }} data-testid="onb-name" />
                <input type="email" placeholder="Votre courriel" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="w-full rounded-lg px-4 py-3 text-[15px]"
                       style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }} data-testid="onb-email" />
                {error && <p className="text-[12px]" style={{ color: "#B91C1C" }}>{error}</p>}
              </div>
            )}

            <div className="mt-12 flex items-center gap-3">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} data-testid="onb-prev"
                        className="text-[13px] px-4 py-2 rounded-full"
                        style={{ color: INK_SOFT, border: `1px solid ${HAIRLINE}` }}>
                  Retour
                </button>
              )}
              {step < 2 ? (
                <button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="onb-next"
                        className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium disabled:opacity-40"
                        style={{ background: INK, color: IVORY }}>
                  Continuer <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </button>
              ) : (
                <button onClick={submit} disabled={saving || !email} data-testid="onb-submit"
                        className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium disabled:opacity-40"
                        style={{ background: INK, color: IVORY }}>
                  {saving ? "Envoi…" : "Recevoir mon diagnostic"} <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
